import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  API_PATHS,
  SSE_EVENT_CHANGE,
  SSE_EVENT_RESET,
  type ChangelogEntryDto,
} from '@betreuung/shared';
import { queryKeys } from './queryKeys.js';

/**
 * Live sync.
 *
 * The server streams changelog entries; each carries its own id, so the browser's
 * built-in `Last-Event-ID` handling gives us gap-free replay after a reconnect
 * without any code of our own.
 *
 * Invalidation is debounced because a single range-clear can produce dozens of
 * entries in one burst, and refetching once afterwards is enough.
 */
const INVALIDATE_DEBOUNCE_MS = 120;

export type SyncStatus = 'connecting' | 'live' | 'offline';

export function useSseSync(enabled: boolean): SyncStatus {
  const client = useQueryClient();
  const [status, setStatus] = useState<SyncStatus>('connecting');
  const pending = useRef({ assignments: false, persons: false, changelog: false, me: false });
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) {
      setStatus('connecting');
      return;
    }

    const flush = () => {
      timer.current = null;
      const next = pending.current;
      pending.current = { assignments: false, persons: false, changelog: false, me: false };
      if (next.assignments) void client.invalidateQueries({ queryKey: queryKeys.assignments.all });
      if (next.persons) void client.invalidateQueries({ queryKey: queryKeys.persons });
      if (next.changelog) void client.invalidateQueries({ queryKey: queryKeys.changelog });
      if (next.me) void client.invalidateQueries({ queryKey: queryKeys.me });
    };

    const schedule = () => {
      if (timer.current !== null) return;
      timer.current = window.setTimeout(flush, INVALIDATE_DEBOUNCE_MS);
    };

    const source = new EventSource(API_PATHS.events);

    source.addEventListener('open', () => setStatus('live'));

    source.addEventListener(SSE_EVENT_CHANGE, (event) => {
      setStatus('live');
      try {
        const entry = JSON.parse((event as MessageEvent<string>).data) as ChangelogEntryDto;
        if (entry.entity === 'assignment') pending.current.assignments = true;
        // A renamed or recoloured person changes how assignments render — and
        // it may be *this* person, whose own role decides what the app offers.
        // `me` has a five-minute staleTime and nothing else invalidates it, so
        // without this a promoted account would sit in a read-only interface
        // with no error to explain it and no request that could self-heal.
        if (entry.entity === 'person') {
          pending.current.persons = true;
          pending.current.assignments = true;
          pending.current.me = true;
        }
        pending.current.changelog = true;
        schedule();
      } catch {
        // A malformed event must not kill the stream; refetch defensively.
        pending.current = { assignments: true, persons: true, changelog: true, me: true };
        schedule();
      }
    });

    // Too many missed events to replay: start over from the server's state.
    source.addEventListener(SSE_EVENT_RESET, () => {
      setStatus('live');
      void client.invalidateQueries();
    });

    source.addEventListener('error', () => {
      // EventSource reconnects on its own; surface the gap in the meantime.
      setStatus(source.readyState === EventSource.CLOSED ? 'offline' : 'connecting');
    });

    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = null;
      source.close();
    };
  }, [client, enabled]);

  return status;
}

/** Browser online/offline, used together with the stream status for the banner. */
export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return online;
}
